import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import type { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import AppToast from "../components/AppToast";
import { usePageTitle } from "../components/PageTitle";
import { applyDirection } from "../lib/direction";

const API = import.meta.env.VITE_API_BASE || "/api";

export default function AdminLogin() {
  const { t, i18n } = useTranslation();
  usePageTitle(t("login.adminLoginTitle"));

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    applyDirection(i18n.language);
  }, [i18n.language]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    toast.current?.clear();
    setSubmitting(true);

    try {
      const r = await fetch(`${API}/public/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (!r.ok) {
        const j: { error?: string } = await r.json().catch(() => ({}));
        const code = (j?.error || "").toString();
        toast.current?.show({
          severity: "error",
          summary: t("login.loginFailedTitle"),
          detail: code.includes("invalid_credentials")
            ? t("login.invalidCredentials")
            : t("login.loginFailedGeneric"),
          life: 5000,
        });
        return;
      }

      const j = await r.json();
      localStorage.setItem("admin_token", j.token);
      window.location.href = "/admin";
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell width="center" homeHref="/admin/login">
      <AppToast ref={toast} position="top-right" />

      <section className="auth-card rise">
        <p className="auth-card__eyebrow">{t("app.admin")}</p>
        <h1 className="auth-card__title">{t("login.adminLoginTitle")}</h1>

        <form onSubmit={onSubmit}>
          <div className="field">
            <label className="field__label" htmlFor="username">
              {t("login.username")}
            </label>
            <InputText
              id="username"
              className="w-full"
              placeholder={t("login.usernamePlaceholder")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="password">
              {t("login.password")}
            </label>
            <Password
              id="password"
              className="w-full"
              inputClassName="w-full"
              placeholder={t("login.passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              toggleMask
              feedback={false}
              autoComplete="current-password"
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            style={{ marginTop: "var(--space-5)" }}
            loading={submitting}
            disabled={!username || !password}
            label={t("login.login")}
            icon="pi pi-sign-in"
          />
        </form>

        <p
          className="field__hint"
          style={{ textAlign: "center", marginTop: "var(--space-5)" }}
        >
          <Link to="/">{t("login.returnToPlayer")}</Link>
        </p>
      </section>
    </AppShell>
  );
}
